import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Eye, ArrowUpRight } from 'lucide-react';
import PrivateBookingBadge from '../../shared/PrivateBookingBadge';
import { isPrivateBooking } from '../../../utils/bookingSource';

const RecentBookings = ({ bookings = [] }) => {
  const navigate = useNavigate();

  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '$0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
    if (isNaN(numAmount) || !isFinite(numAmount)) return '$0.00';
    return `$${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300';
      case 'confirmed':
        return 'bg-sky-100 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300';
      case 'pending':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300';
      case 'cancelled':
        return 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  return (
    <div className="lg:col-span-2 rounded-2xl border border-gray-200/80 bg-white dark:border-gray-700 dark:bg-gray-900 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 sm:px-5 pt-4 sm:pt-5 pb-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-50">Recent bookings</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Latest marketplace activity</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/bookings')}
          className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium inline-flex items-center gap-1 shrink-0"
        >
          View all
          <ArrowUpRight className="w-4 h-4" />
        </button>
      </div>
      {bookings.length > 0 ? (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {bookings.map((booking) => (
            <div
              key={booking.id}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/admin/bookings/${booking.id}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/admin/bookings/${booking.id}`);
                }
              }}
              className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 hover:bg-gray-50/80 dark:hover:bg-gray-800/40 cursor-pointer transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-50 truncate">
                    {booking.booking_reference || `#${String(booking.id).substring(0, 8)}`}
                  </p>
                  {isPrivateBooking(booking) && <PrivateBookingBadge />}
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${getStatusColor(booking.status)}`}>
                    {String(booking.status || '').replace(/_/g, ' ')}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                  {booking.client && <span className="truncate">{booking.client.name}</span>}
                  {booking.event_date && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(booking.event_date)}
                    </span>
                  )}
                  {booking.subtotal_amount != null && (
                    <span className="font-semibold text-gray-900 dark:text-gray-50 tabular-nums">
                      {formatCurrency(booking.subtotal_amount)}
                    </span>
                  )}
                </div>
              </div>
              <Eye className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-14 px-6">
          <Calendar className="w-9 h-9 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">No recent bookings</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">New bookings will appear here.</p>
        </div>
      )}
    </div>
  );
};

export default RecentBookings;
